/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-restricted-syntax */
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import {
    convert,
    createCatalogueList,
    generator,
} from '@tencent/slide-markdown-converter';
import { ShapeType, SlideType } from '@tencent/slide-markdown-converter/src/define';
import { DataType, IDrawingShapeDataModel, INoteSlideData, IPresentationData, IPresentationDataModel, ISlideData, ISlideDataModel } from '@tencent/slide-sdk-model';
import cloneDeep from 'lodash/cloneDeep';
import { marked } from 'marked';
import { addImageToMarkdownPage, convertChartData, countNumbersInText, extractSlideByTag, fuzzySearch, getAllImagesInSlide, titleToMarkdown, WritingContext } from './data-utils';
import { fetchTemplateById, fetchTemplateByIdByNonVip, fetchTemplateFromCdn } from './fetch-template';
import { ErrorCode as AIErrorCode, EventType, SSEService } from './sse';

import { ApiError, ErrorCode } from '@src/framework/docs-api/error-code';
import { generateChart, getImagePosition } from '@src/slide/application/service/ai/core/api/api';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { ISlideContentLayoutAnalyzerService } from '@src/slide/application/service/slide-content-layout-analyzer/core/slide-content-layout-analyzer-service.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { dataValidateAndReport } from '@src/slide/domain/slide-app/utils/data-validate';
import { ChartOptions, insertChart } from '@src/slide/domain/ui-model/element-modeles/chart-models';
import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import type { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { getToggleStatusByType, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { emuToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { getShapeTag } from '@tencent/slide-markdown-converter/src/schema';
import groupBy from 'lodash/groupBy';
import { reportUtils } from '../../ai/core/api/report-utils';
import { i18n } from '../../edit-extension/edit-extension-export';
import { PageText, PageType } from '../../slide-content-layout-analyzer/core/algorithm/data-structure/page-content';
import { ITemplateContentAnalyzerService } from '../../template-content-analyzer-service/index.interface';
import { ImageGenerator } from './image-generator';
import Aegis from '@src/slide/infrastructure/module/dependent/report/platform/aegis';
import { IPcLeftThumbnailListService } from '@src/slide/application/service/pc-left-thumbnail-list/core/type';

interface ExtractedSlide {
    slideId: string;
    type: SlideType;
    title: string;
    markdown: string;
    allText: string;
}

interface ProgressEvent {
    // stage: 'slide' | 'image';
    total: number;
    left: number;
}

export enum ReportKey {
    AI_GENERATE_OUTLINE = 'AI_GENERATE_OUTLINE',
    AI_GENERATE_POSITION = 'AI_GENERATE_POSITION',
    AI_GENERATE_IMAGE = 'AI_GENERATE_IMAGE',
    AI_GENERATE_WAIT_FOR_READY = 'AI_GENERATE_WAIT_FOR_READY',
    AI_GENERATE_WAIT_FOR_TEMPLATE = 'AI_GENERATE_WAIT_FOR_TEMPLATE',
    AI_GENERATE_SINGLE_PAGE = 'AI_GENERATE_SINGLE_PAGE',
}

export class AIGenerator {
    public static ENABLE_CHART = getToggleStatusByType(ToggleType.AI_CHART);

    /** 新增页时是否选中 */
    public shouldFocusOnAddPage = true;
    /** 推断出的页面结构 */
    private extractedSlides: Array<ExtractedSlide>;
    private initPromise: Promise<void>;
    private imageGenerator = new ImageGenerator();
    private coverTemplatePresentationData: IPresentationData | undefined = undefined;

    private slidePageCacheMap = new Map<string, string>();

    public constructor(
        private uiDataService: IUiDataService,
        private contentLayoutAnalyzer: ISlideContentLayoutAnalyzerService,
        private selectionService: ISelectionService,
        private templateContentAnalyzer: IDynamicService<ITemplateContentAnalyzerService>,
        private slideListService?: IPcLeftThumbnailListService,
    ) {
        this.initPromise = this.initBaseTemplate();

        if (__IS_DEV__) {
            (window as any).aiGenerator = this;
        }
    }

    public async waitForReady() {
        return this.initPromise;
    }

    /** 初始化基础模板 */
    public async initBaseTemplate() {
        let presentationData: IPresentationData;

        try {
            presentationData = await fetchTemplateFromCdn('component');
        } catch (error) {
            const BASE_TEMPLATE_DOC_ID = '1fw5ztun97bgtyp6z4qxibcysy';
            const slideFileData = await fetchTemplateById(BASE_TEMPLATE_DOC_ID);
            presentationData = convert(cloneDeep(slideFileData));
        }

        generator.updateBaseTemplate(presentationData);
    }

    public async loadCoverTemplate() {
        if (!this.coverTemplatePresentationData) {
            this.coverTemplatePresentationData = await fetchTemplateFromCdn('cover');
        }
        generator.updateTemplate(this.coverTemplatePresentationData, 'cover_template');
    }

    /** 获取模板数据 */
    public async getTemplateDataById(templateId: string) {
        try {
            const slideFileData = await fetchTemplateByIdByNonVip(templateId);
            const presentationData = convert(cloneDeep(slideFileData));
            return presentationData;
        } catch (error) {
            throw new ApiError(ErrorCode.FetchTemplateErr);
        }
    }

    public async updateTemplateDataById(templateId: string) {
        const presentationData = await this.getTemplateDataById(templateId);
        generator.updateTemplate(presentationData, templateId);
    }

    /** 切换模板 */
    public async applyTemplate(templateData: IPresentationData, templateId: string): Promise<void | IPresentationData> {
        await this.waitForReady();

        const result = await this.extractSlideListByTag();

        // 更新应用模版
        generator.updateTemplate(templateData, templateId);

        // 为避免页数据被合并, 逐页生成;
        // 取最后一次的 presentationData, 拼装所有生成的 slideList
        let presentationData: IPresentationData | undefined;

        const slideIdMap: Record<string, string> = {};
        const slideList = result.flatMap<ISlideData>((outline) => {
            if (!outline) return [];

            presentationData = generator.generateSlideFromMarkdownPage(outline.markdown, outline.type).presentationData;
            const { slideList } = presentationData;
            if (slideList[0]) {
                slideIdMap[outline.slideId] = slideList[0].id;
            }
            return slideList;
        });

        if (presentationData) {
            presentationData.slideList = slideList;

            const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
            if (!presentation) {
                presentationData.noteSlideMap = {};
            } else {
                const { noteSlideMap } = presentation.getCloneData();

                const newNoteSlideMap: Record<string, INoteSlideData> = {};
                Object.keys(noteSlideMap).forEach((id) => {
                    const newSlideId = slideIdMap[noteSlideMap[id].slideId];
                    if (!newSlideId) return;

                    noteSlideMap[id].slideId = newSlideId;
                    newNoteSlideMap[id] = noteSlideMap[id];
                });

                presentationData.noteSlideMap = newNoteSlideMap;
            }
        }

        return presentationData;
    }

    /** 大纲请求完整 markdown 正文, 流式插入 */
    public async insertSlides(
        context: WritingContext,
        ignoreCoverPage = false,
        onProgress?: (progressEvent: ProgressEvent) => void,
    ) {
        const { outline, templateId, conversationId, messageId, enableImageGeneration = false } = context;
        reportUtils.start(ReportKey.AI_GENERATE_WAIT_FOR_TEMPLATE, {
            ext1: ignoreCoverPage ? 'new' : 'insert',
            ext2: enableImageGeneration ? 'enable' : 'disable',
        });

        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.CREATE_PPT,
            action: TDWActionType.AI_CLK,
            ver6: context.templateId,
            // 0-列表页，1-详情页新建，2-详情页插入
            ver7: ignoreCoverPage ? 1 : 2,
            // 开启生图
            ver8: enableImageGeneration ? 1 : 0,
        });

        await this.waitForReady();

        return new Promise<void>((resolve, reject) => {
            const reportOutline = reportUtils.start(ReportKey.AI_GENERATE_OUTLINE, {
                ext1: ignoreCoverPage ? 'new' : 'insert',
                ext2: enableImageGeneration ? 'enable' : 'disable',
            });

            type Chunk = { type: SlideType; markdown: string };
            const queue: Chunk[] = [];
            const enqueue = (chunk: string | Chunk) => {
                if (typeof chunk !== 'string') {
                    pageCount += 1;
                    queue.push(chunk);
                    return;
                }
                const markdown = chunk;
                const coverRegex = /^# /;
                const sessionRegex = /^## /;
                const pageRegex = /^### /;

                let type: SlideType = SlideType.page;
                if (pageRegex.test(markdown)) {
                    type = SlideType.page;
                }
                if (sessionRegex.test(markdown)) {
                    type = SlideType.session;
                }
                if (coverRegex.test(markdown)) {
                    type = SlideType.cover;
                }
                pageCount += 1;
                queue.push({ markdown, type });
            };

            let isFirstPage = true;
            let currentChunk = '';
            let completed = false;
            /** 生成页数 */
            let pageCount = 0;
            const addPagePromiseList: Array<Promise<void>> = [];

            // eslint-disable-next-line complexity
            SSEService.expandOutline({ outline, conversationId, messageId }, ({ data, type }) => {
                if (completed) {
                    window.log.error('receive unexpected data after completed', type);
                    return;
                }

                switch (type) {
                    case EventType.CONTENT: {
                        const line = data.content?.content?.text;
                        if (!line) return;

                        const headingRegex = /^(#|##|###) /;

                        if (headingRegex.test(line)) {
                            if (currentChunk) {
                                if (isFirstPage) {
                                    isFirstPage = false;

                                    // 后台已经生成了封面, 跳过首页
                                    if (!ignoreCoverPage) enqueue(currentChunk);
                                    // 首页, 插入目录
                                    enqueue({
                                        type: SlideType.catalogue,
                                        markdown: createCatalogueList(outline),
                                    });
                                } else {
                                    enqueue(currentChunk);
                                }
                                commit();
                            }
                            currentChunk = '';
                        }
                        currentChunk += line;
                        return;
                    }

                    case EventType.DONE:
                    case EventType.STOP: {
                        window.log.info(`receive ${type}`);
                        completed = true;
                        if (currentChunk) {
                            enqueue(currentChunk);
                            enqueue({ type: SlideType.ending, markdown: '' });
                            commit();
                            currentChunk = '';
                        }

                        reportOutline.success({
                            ext3: `${pageCount}`,
                        });

                        return;
                    }

                    case EventType.ERROR: {
                        if (data.code === AIErrorCode.MODEL_BUSY) {
                            DUISnackbar.closeAll();
                            setTimeout(() => {
                                DUISnackbar.show({
                                    message: data.message,
                                    type: 'warning',
                                    closable: true,
                                    autoClose: false,
                                    action: i18n.t('重试'),
                                    onClickAction: async () => {
                                        const presentationStore = await SlideDataServices.SDKDataServices
                                            .PresentationStoreService.promise();
                                        const presentation = presentationStore.getPresentation();
                                        const { slideList } = presentation;
                                        for (let i = 1; i < slideList.length; i += 3) {
                                            const slidesToRemove = slideList.slice(i, i + 3);
                                            presentation.api.removeSlides(slidesToRemove);
                                        }
                                        eventBus.emit(EventKeys.RETRY_AI_WRITING);
                                    },
                                });
                            }, 1);
                        } else {
                            DUISnackbar.show({
                                message: data.message || i18n.t('未知错误，请稍后重试'),
                                type: 'warning',
                                closable: true,
                                autoClose: false,
                            });
                        }

                        tdw({
                            opername: TDWOperationType.DOC_PPT_AI,
                            module: TDWModuleType.CREATE_PPT,
                            action: TDWActionType.AI_FAIL,
                            ver6: context.templateId,
                            ver7: ignoreCoverPage ? 1 : 2,
                            ver8: enableImageGeneration ? 1 : 0,
                        });
                        reportOutline.fail({
                            ext3: `${data.code}`,
                        });

                        reject(new ApiError(ErrorCode.ServerGenerationErr));
                        return;
                    }

                    default:
                        break;
                }
            });

            let templateReady = false;
            this.getTemplateDataById(templateId)
                .then((templateData) => {
                    generator.updateTemplate(templateData, templateId);
                    window.log.info('fetch template success ', templateId);
                    templateReady = true;

                    if (queue.length > 0) commit();
                })
                .catch(reject);

            let totalImageCount = 0;
            let totalChartCount = 0;
            let imagePageCount = 0;
            let chartPageCount = 0;
            // eslint-disable-next-line complexity
            const commit = () => {
                if (!templateReady) return;
                const chunk = queue.shift();
                if (!chunk) return;

                const { type, markdown } = chunk;
                const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()!.getPresentation();
                // 每章节重置
                if (type === SlideType.session) {
                    imagePageCount = 0;
                    chartPageCount = 0;
                }

                const getExtraResourse = (): 'chart' | 'image' | 'none' => {
                    if (type !== SlideType.page) return 'none';

                    // 页标题中有序号，多算了一次计数
                    const numberCount = countNumbersInText(markdown) - 1;
                    if (AIGenerator.ENABLE_CHART && chartPageCount < 2 && numberCount >= 8) return 'chart';
                    if (enableImageGeneration && imagePageCount < 1) return 'image';
                    if (AIGenerator.ENABLE_CHART && chartPageCount < 1 && numberCount >= 4) return 'chart';
                    return 'none';
                };
                const extraResourse = getExtraResourse();

                switch (extraResourse) {
                    case 'image': {
                        imagePageCount += 1;
                        totalImageCount += 1;
                        // 先插入仅有标题的页占位
                        const slides = this.addPage(presentation, markdown, type, { onlyTitle: true });
                        // 请求带图位的页替换占位页
                        const promise = this.replaceSlidesWithPageHasImage(slides, presentation, markdown, type);
                        addPagePromiseList.push(promise);
                        break;
                    }
                    case 'chart': {
                        chartPageCount += 1;
                        totalChartCount += 1;
                        this.addPage(presentation, markdown, type, { needCreateChart: true });
                        break;
                    }
                    default: {
                        this.addPage(presentation, markdown, type);
                        break;
                    }
                }

                if (queue.length > 0) {
                    commit();
                    return;
                }

                if (completed) void onComplete();
            };

            const onComplete = async (): Promise<void> => {
                if (addPagePromiseList.length) {
                    const reportPosition = reportUtils.start(ReportKey.AI_GENERATE_POSITION);
                    await Promise.all(addPagePromiseList);
                    reportPosition.success();
                }

                const slideList = this.uiDataService.presentation?.children;
                tdw({
                    opername: TDWOperationType.DOC_PPT_AI,
                    module: TDWModuleType.CREATE_PPT,
                    action: TDWActionType.AI_SUCCESS,
                    // 生成页数
                    ver5: pageCount,
                    ver6: context.templateId,
                    // 0-列表页，1-详情页新建，2-详情页插入
                    ver7: ignoreCoverPage ? 1 : 2,
                    ver8: enableImageGeneration ? 1 : 0,
                    // 实际生成的页数
                    ver9: slideList?.length,
                    // 最后一页的数据类型
                    ver10: slideList?.[slideList.length - 1]?.getAIType(),
                    // 生图总数
                    ver11: totalImageCount,
                    // 图表总数
                    ver12: totalChartCount,
                });

                if (!this.imageGenerator.isRunning) {
                    resolve();
                    return;
                }

                const { task } = this.imageGenerator;
                const total = task.pendingTaskQueue.length + task.activeTasks;
                onProgress?.({ total, left: total });
                const reportImage = reportUtils.start(ReportKey.AI_GENERATE_IMAGE);
                const interval = setInterval(() => {
                    const left = task.pendingTaskQueue.length + task.activeTasks;
                    onProgress?.({ total, left });

                    if (left === 0) {
                        clearInterval(interval);
                        reportImage.success();
                        resolve();
                    }
                }, 500);
            };
        });
    }

    public async generateSlidePage(outline: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const reporter = reportUtils.start(ReportKey.AI_GENERATE_SINGLE_PAGE);
            let completed = false;
            let resultOutline = '';
            SSEService.generateSlidePage({ content: outline }, ({ data, type }) => {
                if (completed) {
                    window.log.error('receive unexpected data after completed', type);
                    return;
                }

                switch (type) {
                    case EventType.CONTENT: {
                        const line = data.content?.content?.text;
                        if (!line) return;

                        resultOutline += line;
                        return;
                    }

                    case EventType.DONE:
                    case EventType.STOP: {
                        completed = true;
                        reporter.success();
                        resolve(resultOutline);
                        return;
                    }

                    case EventType.ERROR: {
                        completed = true;
                        reporter.fail({
                            ext3: `${data.code}`,
                        });
                        reject();
                        return;
                    }

                    default:
                        break;
                }
            });
        });
    }

    public async generateSlidePageWithCache(outline: string): Promise<string | null> {
        const resFromCache = this.slidePageCacheMap.get(outline);
        if (resFromCache) return resFromCache;

        try {
            const result = await this.generateSlidePage(outline);

            // 内容过短不缓存
            if (result.length > 10) {
                this.slidePageCacheMap.set(outline, result);
            }
            return result;
        } catch (error) {
            // 内部已做过上报
            return null;
        }
    }

    /** 提取当前所有 slide 为 markdown 文本, 优先使用标签, 无标签使用推测 */
    public async extractSlideContentAsMarkdownText(): Promise<string> {
        const [analysisExtractedSlides, tagExtractedSlides] = await Promise.all([
            this.extractSlideByAnalysis(),
            this.extractSlideListByTag(),
        ]);

        // 优先标签, 推断结果兜底
        this.extractedSlides = tagExtractedSlides.map((extractedSlide, index) =>
            extractedSlide ?? analysisExtractedSlides[index]);

        const extractedMarkdown = this.extractedSlides
            // TODO：用户文档可能出现只提取到正文但没有标题的情况
            .map(extractedSlide => extractedSlide.markdown)
            .filter(markdown => !!markdown)
            .join('\n');

        window.log.info('extractSlideContentAsMarkdownText', extractedMarkdown);
        return extractedMarkdown;
    }

    /** 提取当前所有 slide 为 JsonString 文本 */
    public async extractSlideContentAsJsonString(): Promise<string> {
        const analysisExtractedSlides = await this.extractSlideByAnalysis(false);

        const extractedJson = analysisExtractedSlides
            .map((extractedSlide, index) => ({ pageNum: index + 1, text: extractedSlide.allText }))
            .filter(obj => obj.text.trim().length > 0);
        if (extractedJson.length === 0) return '';
        return JSON.stringify(extractedJson);
    }

    /** 将 AI 生成的演讲稿插入备注 */
    public insertSpeechTextIntoNotes(markdownText: string) {
        const hasEndingPage = this.extractedSlides.some(res => res.type === SlideType.ending);

        // 1. 将演讲稿切分为段
        const tokens = marked
            .lexer(markdownText)
            .filter((token): token is marked.Tokens.Heading | marked.Tokens.Paragraph =>
                token.type === 'heading' || token.type === 'paragraph');

        type PageNote = {
            type: SlideType;
            title: string;
            notes: string[];
        };
        const pageNotes: PageNote[] = [];
        let lastNote: PageNote | null = null;

        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            // 按 Heading 分割为页
            if (token.type === 'heading') {
                let type: SlideType;
                if (token.depth === 1) {
                    type = SlideType.cover;
                } else if (token.depth === 2) {
                    type = SlideType.session;
                } else {
                    type = SlideType.page;
                }

                const note: PageNote = {
                    type,
                    title: token.text,
                    notes: [],
                };
                pageNotes.push(note);
                lastNote = note;
                continue;
            }

            // 两个 Heading 之间的 Paragraph 作为上一页的内容

            // 最后一段 Paragraph，如果上一页有内容，则独立作为尾页
            if (hasEndingPage && i === tokens.length - 1 && lastNote && lastNote.notes.length > 0) {
                pageNotes.push({
                    type: SlideType.ending,
                    title: '',
                    notes: [token.text],
                });
                continue;
            }

            lastNote?.notes.push(token.text);
        }

        // 2. 查找对应的标题, 插入备注
        const unmatchedNotes: PageNote[] = [];
        const extractedSlideMap = groupBy(this.extractedSlides, s => s.type) as Record<SlideType, ExtractedSlide[]>;
        transaction(() => {
            pageNotes.forEach((pageNote) => {
                if (pageNote.notes.length === 0) return;

                const matched = this.matchExtractedSlide(extractedSlideMap, pageNote.type, pageNote.title.trim());

                if (!matched) {
                    unmatchedNotes.push(pageNote);
                    return;
                }

                const slideUiModel = this.uiDataService.slideModelList.find(slide => slide.id === matched.slideId);
                if (!slideUiModel) {
                    unmatchedNotes.push(pageNote);
                    return;
                }

                slideUiModel.noteData.updateNoteText(pageNote.notes);
            });
        });

        window.log.info(`Not match notes: ${unmatchedNotes.length}`);

        if (pageNotes.length === unmatchedNotes.length) {
            throw new ApiError(ErrorCode.NoteNotMatchErr);
        }
    }

    public insertPresentation(presentationData: IPresentationData) {
        const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
        if (presentation) {
            presentation.api.addPresentation(presentationData, presentation.slideList.length);
        }
    }

    public replacePresentation(presentationData: IPresentationData) {
        const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
        presentation?.api.replacePresentation(presentationData);
    }

    public async createChart(markdown: string, slideData: ISlideDataModel, shape: IDrawingShapeDataModel) {
        const resp = await generateChart({ content: markdown });
        if (!resp?.gen_chart) return;

        const slideUIModel = this.uiDataService.presentation.children.find(
            // force break
            slideModel => slideModel.sdkData?.id === slideData.id);
        if (!slideUIModel?.sdkData) return;

        const chartTypeMap = {
            柱状图: PresetChartType.COLUMN_CLUSTERED,
            折线图: PresetChartType.LINE_STANDARD,
            条形图: PresetChartType.BAR_CLUSTERED,
            饼图: PresetChartType.PIE,
        };
        let chartType = chartTypeMap[resp.chart_type] ?? PresetChartType.COLUMN_CLUSTERED;
        const { title, tableData } = generator.parseMarkdownTable(resp.content);
        const chartData = convertChartData(tableData);

        // 折线图至少有 3 个类别,否则用柱状图兜底
        if (chartType === PresetChartType.LINE_STANDARD) {
            if (chartData.categories.length < 3) {
                chartType = PresetChartType.COLUMN_CLUSTERED;
            }
        }

        const dataCount = chartData.categories.length * chartData.seriesNames.length;

        Aegis.getInstance().aegis.reportEvent({
            name: 'GEN_CHART_RES',
            ext1: `${dataCount}`,
        });

        if (dataCount <= 0) return;

        const options: ChartOptions = {
            chartData: convertChartData(tableData),
            showLegend: true,
            showDataLabels: true,
            titleText: title,
        };

        const chartFrame = {
            width: emuToCanvas(shape.props.spPr.xfrm?.ext?.cx ?? 0),
            height: emuToCanvas(shape.props.spPr.xfrm?.ext?.cy ?? 0),
            left: emuToCanvas(shape.props.spPr.xfrm?.off?.x ?? 0),
            top: emuToCanvas(shape.props.spPr.xfrm?.off?.y ?? 0),
        };

        transaction(() => {
            slideUIModel.sdkData!.api.deleteShapes([shape]);
            insertChart(slideUIModel, chartType, chartFrame, options);
        });
    }

    public async restructureSlide(
        slide: ISlideDataModel,
        candidateCount = 0,
        rewrite = false,
    ): Promise<IPresentationData | void> {
        let outline = extractSlideByTag(slide);

        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.PAGE_BEAUTY,
            action: TDWActionType.AI_START,
            // 页类型 0: AI页 1: 用户页
            ver6: outline ? 0 : 1,
        });

        if (!outline) {
            // 来自用户页
            const extractedSlides = await this.extractSlideByAnalysis();
            const extractedSlide = extractedSlides.find(extractedSlide => extractedSlide.slideId === slide.id);
            if (!extractedSlide) return;

            outline = extractedSlide;

            if (outline.type === SlideType.page) {
                if (outline.markdown.length < 50) return;

                const urls = getAllImagesInSlide(slide);
                let newMarkdown = await this.generateSlidePageWithCache(outline.markdown);
                if (!newMarkdown) return;

                if (urls.length > 0) newMarkdown = addImageToMarkdownPage(newMarkdown, urls);

                outline.markdown = newMarkdown;
            }
        }

        const { markdown } = outline;

        if (outline.type !== SlideType.ending && markdown.length === 0) return;

        if (outline.type === SlideType.page) {
            await this.waitForReady();
            const presentationModel = SlideDataServices.SDKDataServices.PresentationStoreService()!.getPresentation();
            const currentPresentationData = presentationModel.getCloneData();
            currentPresentationData.slideList = [];
            generator.updateTemplate(currentPresentationData, 'currentDoc');
        } else {
            await this.loadCoverTemplate();
        }

        const { presentationData } = generator.generateSlideFromMarkdownPage(markdown, outline.type, {
            pageCount: candidateCount,
        });

        return presentationData;
    }

    public async insertSlidePage(
        markdown: string,
        slideType: SlideType = SlideType.page,
        options?: any,
    ): Promise<void> {
        await this.waitForReady();
        // 如果处于左侧列表 section 的选中态 ，并且 section 未展开，需要展开 section 后再添加
        // (目前只有PC端环境下才会有 section) (slideListService 是 PcLeftThumbnailListService 的实例)
        if (this.slideListService) {
            const curSelectedSectionIndex = this.slideListService.getSelectedSectionIndex();
            if (curSelectedSectionIndex > - 1) {
                const isFolded = (this.slideListService.getSectionFoldedState() || [])[curSelectedSectionIndex];
                isFolded && this.slideListService.toggleSectionFolded(curSelectedSectionIndex);
            }
        }
        const presentationModel = SlideDataServices.SDKDataServices.PresentationStoreService()!.getPresentation();
        const currentPresentationData = presentationModel.getCloneData();
        currentPresentationData.slideList = [];
        generator.updateTemplate(currentPresentationData, 'currentDoc');
        const index = this.selectionService.currentSlideIndex ?? 0;
        this.addPage(presentationModel, markdown, slideType, {
            toIndex: index + 1,
            ...options,
        });
    }

    /** 通过标签提取页面文本, 无标签的页会留空 */
    private async extractSlideListByTag(): Promise<Array<ExtractedSlide | null>> {
        const presentationStore = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const presentation = presentationStore.getPresentation();

        return presentation.slideList.map(extractSlideByTag);
    }

    /** 通过结构推断页面, 提取标题/内容 */
    // eslint-disable-next-line complexity
    private async extractSlideByAnalysis(containsStruct = true): Promise<Array<ExtractedSlide>> {
        console.time('extractSlideByAnalysis');
        const { presentation } = this.uiDataService;
        const pageTexts = await this.contentLayoutAnalyzer.analysisTextResult(presentation);
        const userAnalyzerResult = await this.templateContentAnalyzer.promise().then(async (module) => {
            const presentationModel = this.uiDataService.presentation;
            const userAnalyzerResult = await module.analyzerUserDocs({
                templatePresentation: presentationModel,
            });

            console.info(userAnalyzerResult);
            return userAnalyzerResult;
        });
        const {
            userDocCoverResult,
            userDocContentsResult,
            userDocSectionResult,
            userDocContentResult,
            userDocBackCoverResult,
        } = userAnalyzerResult;

        // eslint-disable-next-line complexity
        pageTexts.forEach((pageText) => {
            if (userDocCoverResult && pageText.pageModel.id === userDocCoverResult.pageModel?.id) {
                pageText.pageType = PageType.CoverPage;
                if (userDocCoverResult.ctrTitleLeaf) {
                    pageText.texts = [userDocCoverResult.ctrTitleLeaf];
                }
                return;
            }

            if (userDocContentsResult && pageText.pageModel.id === userDocContentsResult.pageModel?.id) {
                pageText.pageType = PageType.TableOfContents;
                if (userDocContentsResult.titleLeafList) {
                    pageText.texts = userDocContentsResult.titleLeafList;
                }
                return;
            }

            if (userDocSectionResult) {
                for (const userDocSection of userDocSectionResult) {
                    if (pageText.pageModel.id === userDocSection.pageModel?.id) {
                        pageText.pageType = PageType.SectionDivider;
                        if (userDocSection.ctrTitleLeaf) {
                            pageText.texts = [userDocSection.ctrTitleLeaf];
                        }
                        return;
                    }
                }
            }

            if (userDocBackCoverResult && pageText.pageModel.id === userDocBackCoverResult.pageModel?.id) {
                pageText.pageType = PageType.Acknowledgments;
                return;
            }

            pageText.pageType = PageType.Content;
        });

        console.timeEnd('extractSlideByAnalysis');
        return pageTexts.map(pageText => this.convertPageText(pageText, containsStruct));
    }

    private convertPageText(page: PageText, containsStruct = true): ExtractedSlide {
        const slideId = page.pageModel.sdkData?.id ?? '';
        if (!containsStruct) {
            // 不需要目录结构，都作为页
            const [firstNode, ...content] = page.texts;
            const title = (firstNode?.text ?? '').trim();
            const allText = `${title} ${content.map(t => t.text).join(' ')}`;
            const markdown = `### ${allText}`;
            return {
                slideId,
                type: SlideType.page,
                title: '',
                markdown,
                allText,
            };
        }

        // 目录
        if (page.pageType === PageType.TableOfContents) {
            return {
                slideId,
                type: SlideType.catalogue,
                title: '',
                markdown: page.texts.map(text => `- ${text.text}`).join('\n'),
                allText: '',
            };
        }

        // 尾页
        if (page.pageType === PageType.Acknowledgments) {
            return {
                slideId,
                type: SlideType.ending,
                title: '',
                markdown: '',
                allText: '',
            };
        }

        // 封面
        if (page.pageType === PageType.CoverPage) {
            const title = page.texts
                .map(t => t.text)
                .join(' ')
                .trim();

            return {
                slideId,
                type: SlideType.cover,
                title,
                markdown: titleToMarkdown(title, SlideType.cover),
                allText: title,
            };
        }

        // 封面
        if (page.pageType === PageType.SectionDivider) {
            const title = page.texts
                .map(t => t.text)
                .join(' ')
                .trim();

            return {
                slideId,
                type: SlideType.session,
                title,
                markdown: titleToMarkdown(title, SlideType.session),
                allText: title,
            };
        }

        // 页
        const [firstNode, ...content] = page.texts;
        const title = (firstNode?.text ?? '').trim();
        const markdown = `${titleToMarkdown(title, SlideType.page)}${content.map(t => t.text).join(' ')}`;
        const allText = `${title} ${content.map(t => t.text).join(' ')}`;
        return {
            slideId,
            type: SlideType.page,
            title,
            markdown,
            allText,
        };
    }

    /** 创建单页 (内容溢出时可能分页) */
    // eslint-disable-next-line complexity
    private addPage(
        presentationModel: IPresentationDataModel,
        markdown: string,
        type: SlideType,
        options?: {
            onlyTitle?: boolean;
            toIndex?: number;
            shouldFocusOnAddPage?: boolean;
            needCreateChart?: boolean;
        },
    ): ISlideDataModel[] {
        let md = markdown;
        window.log.info('generateSlideFromMarkdownPage', markdown);

        if (options?.onlyTitle) {
            // eslint-disable-next-line prefer-destructuring
            md = markdown.split('\n')[0];
        }

        const { presentationData, metaInfo } = generator.generateSlideFromMarkdownPage(md, type, {
            pageCount: 1,
            hasChart: options?.needCreateChart,
        });
        presentationData.noteSlideMap = {};

        const pageCount = presentationData.slideList.length;
        if (pageCount === 0) return [];

        // 导入数据前校验，校验仅上报
        dataValidateAndReport(presentationData, 'ai_add_page', DataType.Presentation);

        const shouldFocusOnAddPage = options?.shouldFocusOnAddPage ?? this.shouldFocusOnAddPage;
        if (!shouldFocusOnAddPage) {
            this.selectionService.selectedWithCommit.markSpecialOperate(ESpecialOperationType.keep);
        }

        const toIdx = options?.toIndex ?? presentationModel.slideList.length;
        // `addPresentation` 现在会修改入参 newPresentationData 数据 (layoutMap/master/theme...)
        // 因此深拷贝一份避免影响新页的生成
        const { slideIdsArr } = presentationModel.api.addPresentation(cloneDeep(presentationData), toIdx);

        // 根据关键词请求生图
        for (const { keyword, shapeId } of metaInfo.imageKeyArr) {
            for (let index = 0; index < slideIdsArr.length; index++) {
                const slideData = presentationData.slideList[index];
                const idMap = slideIdsArr[index];

                const realSlideId = idMap.get(slideData.id);
                const realShapeId = idMap.get(shapeId);
                if (!realSlideId || !realShapeId) continue;

                const shape = presentationModel.getSlideById(realSlideId)?.getShapeById(realShapeId);
                if (!shape) continue;

                if (shape.type === DataType.Picture || shape.type === DataType.Shape) {
                    void this.imageGenerator.create(shape, keyword);
                }
                // 已找到 shape，无论有没有成功创建生图任务，都应该 break 继续下一个 keyword；
                break;
            }
        }

        // 异步生成图表
        if (options?.needCreateChart) {
            for (let index = 0; index < slideIdsArr.length; index++) {
                const slideData = presentationData.slideList[index];
                const idMap = slideIdsArr[index];

                const realSlideId = idMap.get(slideData.id);
                if (!realSlideId) continue;

                const slide = presentationModel.getSlideById(realSlideId);
                if (!slide) continue;

                const shape = slide.shapeList.find((shape) => {
                    const tag = getShapeTag(shape.getCloneData());
                    return tag?.type === ShapeType.chart;
                });
                if (!shape) continue;

                void this.createChart(markdown, slide, shape);
                break;
            }
        }

        return presentationModel.slideList.slice(toIdx, toIdx + pageCount);
    }

    /** 请求带图位的页替换占位页 */
    private async replaceSlidesWithPageHasImage(
        slides: ISlideDataModel[],
        presentationModel: IPresentationDataModel,
        markdown: string,
        type: SlideType,
    ): Promise<void> {
        const newMarkdown = await this.getImagePosition(markdown);
        const toIndex = presentationModel.slideList.indexOf(slides[0]);
        if (toIndex < 0) return;

        // 替换过程不要抢焦点
        this.selectionService.selectedWithCommit.markSpecialOperate(ESpecialOperationType.keep);
        presentationModel.api.removeSlides(slides);
        this.addPage(presentationModel, newMarkdown, type, { toIndex, shouldFocusOnAddPage: false });
    }

    private async getImagePosition(markdown: string) {
        if (!markdown) return markdown;

        return (await getImagePosition({ input: markdown })) || markdown;
    }

    private matchExtractedSlide(
        extractedSlideMap: Record<SlideType, ExtractedSlide[]>,
        type: SlideType,
        title: string,
    ): ExtractedSlide | null {
        const extractedSlides = extractedSlideMap[type];

        const idx = extractedSlides.findIndex(s => s.title === title);
        // 精确匹配
        if (idx > -1) {
            const matchItem = extractedSlides[idx];
            extractedSlides.splice(idx, 1);
            return matchItem;
        }

        // 模糊匹配
        const [bestResult] = fuzzySearch(
            title,
            extractedSlides.map(s => s.title),
        );
        if (bestResult) {
            window.log.info('fuzzySearch', title, bestResult.text, bestResult.similarity);
            const matchItem = extractedSlides[bestResult.index];
            extractedSlides.splice(bestResult.index, 1);
            return matchItem;
        }

        return null;
    }
}
